import { useEffect, useId, useRef, useState } from 'react';
import { useSeasonalFlags } from '@/shared/providers/SeasonalFlagsProvider';
import { cn } from '@/shared/utils/utils';

const BULB_COLORS = [
  '#dc2626', // Festive red
  '#eab308', // Warm gold
  '#16a34a', // Pine green
  '#3b82f6', // Frost blue
  '#ec4899', // Berry pink
] as const;

const DESKTOP_SWAG_WIDTH = 76;
const MOBILE_SWAG_WIDTH = 40;
const DESKTOP_BREAKPOINT = 1024;
const VIEW_HEIGHT = 30;

interface ChristmasLightsProps {
  className?: string;
}

/**
 * A faded watermark string of colorful Christmas lights between the header logo and user identity,
 * keeping the same soft opacity as `xmas-tree.svg` and `xmas-sleigh.svg`.
 */
export function ChristmasLights({ className }: ChristmasLightsProps) {
  const { xmasEnabled } = useSeasonalFlags();
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, isMobile: false });
  const gradientPrefix = useId();

  useEffect(() => {
    if (!xmasEnabled) return;
    const el = containerRef.current;
    if (!el) return;

    const update = () =>
      setSize({
        width: el.clientWidth,
        isMobile: window.innerWidth < DESKTOP_BREAKPOINT,
      });
    update();

    const observer = new ResizeObserver(update);
    observer.observe(el);
    window.addEventListener('resize', update);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [xmasEnabled]);

  if (!xmasEnabled) return null;

  const { width, isMobile } = size;
  const swagWidth = isMobile ? MOBILE_SWAG_WIDTH : DESKTOP_SWAG_WIDTH;
  const swagCount = Math.max(2, Math.floor(width / swagWidth));
  const step = swagCount > 0 ? width / swagCount : swagWidth;

  const wirePath =
    width > 0
      ? Array.from({ length: swagCount }, (_, i) => {
          const x1 = i * step;
          const cx = (i + 0.5) * step;
          const x2 = (i + 1) * step;
          return `${i === 0 ? `M ${x1.toFixed(1)} 3` : ''} Q ${cx.toFixed(1)} 10 ${x2.toFixed(1)} 3`;
        }).join(' ')
      : '';

  return (
    <div
      ref={containerRef}
      className={cn(
        'christmas-lights-watermark pointer-events-none relative mx-2 h-7 min-w-0 flex-1 select-none lg:mx-4',
        // Desktop name/email floats out of the sidebar; stop before it.
        'lg:mr-48',
        className
      )}
      aria-hidden
    >
      {width > 0 && (
        <svg
          width={width}
          height={VIEW_HEIGHT}
          viewBox={`0 0 ${width} ${VIEW_HEIGHT}`}
          className="overflow-visible"
        >
          <defs>
            {BULB_COLORS.map((color, idx) => (
              <radialGradient key={color} id={`${gradientPrefix}-${idx}`} cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor={color} stopOpacity="0.7" />
                <stop offset="45%" stopColor={color} stopOpacity="0.32" />
                <stop offset="100%" stopColor={color} stopOpacity="0" />
              </radialGradient>
            ))}
          </defs>

          {/* Scalloped hanging cord */}
          <path
            d={wirePath}
            fill="none"
            stroke="currentColor"
            strokeOpacity="0.7"
            strokeWidth="1.6"
            strokeLinecap="round"
          />

          {/* Bulbs hanging at the bottom of each swag */}
          {Array.from({ length: swagCount }, (_, i) => {
            const x = (i + 0.5) * step;
            const y = 6.5;
            const tilt = i % 2 === 0 ? -5 : 5;
            const colorIdx = i % BULB_COLORS.length;
            const color = BULB_COLORS[colorIdx];
            const delay = `-${((i * 0.37) % 2.2).toFixed(2)}s`;

            return (
              <g key={i} transform={`translate(${x.toFixed(1)}, ${y}) rotate(${tilt})`}>
                {/* Soft radial halo behind the glass */}
                <circle
                  cx="0"
                  cy="10"
                  r="12"
                  fill={`url(#${gradientPrefix}-${colorIdx})`}
                  className="christmas-light-halo"
                  style={{ animationDelay: delay }}
                />

                {/* Socket cuff */}
                <rect
                  x="-2.4"
                  y="0"
                  width="4.8"
                  height="3.4"
                  rx="1"
                  fill="currentColor"
                  fillOpacity="0.88"
                />

                {/* Teardrop glass bulb */}
                <g className="christmas-light-bulb" style={{ animationDelay: delay }}>
                  <path
                    d="M -2 3.2
                       C -4.8 5.8 -5 10.8 -3.2 14
                       C -1.8 16.2 1.8 16.2 3.2 14
                       C 5 10.8 4.8 5.8 2 3.2
                       Z"
                    fill={color}
                    fillOpacity="0.48"
                    stroke={color}
                    strokeOpacity="0.72"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />

                  {/* Right-side shading crescent */}
                  <path
                    d="M 0.8 4
                       C 3 6.2 3.3 10.4 1.8 13.4
                       C 2.9 12.6 3.7 10 3.3 6.8
                       C 3 5.1 2 3.9 0.8 4 Z"
                    fill={color}
                    fillOpacity="0.35"
                  />

                  {/* Warm filament / core glow */}
                  <ellipse cx="-0.5" cy="9.2" rx="1.5" ry="2.4" fill={color} fillOpacity="0.65" />

                  {/* Specular highlight dot */}
                  <circle cx="-1.6" cy="7.5" r="1.1" fill="#ffffff" fillOpacity="0.75" />
                </g>
              </g>
            );
          })}
        </svg>
      )}
    </div>
  );
}
